import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { PageHeader } from "../../components/shell/AppShell";
import { Button, SelectField } from "../../components/ui/Controls";
import { AlertBanner, ErrorState, Skeleton, StatusBadge } from "../../components/ui/Feedback";
import {
  DestructiveConfirmDialog,
  ReauthenticationDialog,
  type ReauthenticationCredentials,
} from "../../components/ui/SecurityDialogs";
import { apiErrorMessage, useAuth } from "../../lib/auth";
import { operationsApi, projectsApi, securityApi } from "../../lib/api";
import { backupCopy } from "../data-services/backup-copy";

export function OperationsSettingsPage() {
  const auth = useAuth();
  const [projectId, setProjectId] = useState("");
  const projects = useQuery({
    queryKey: ["projects"],
    queryFn: ({ signal }) => projectsApi.list(signal),
    retry: false,
  });
  const selected = projectId === "" ? projects.data?.items[0]?.id : projectId;
  const policy = useQuery({
    queryKey: ["retention-policy", selected],
    queryFn: ({ signal }) => operationsApi.retentionPolicy(selected ?? "", signal),
    enabled: selected !== undefined,
    retry: false,
  });
  const preview = useQuery({
    queryKey: ["operations-retention", selected],
    queryFn: ({ signal }) => operationsApi.retentionPreview(selected ?? "", signal),
    enabled: selected !== undefined,
    retry: false,
  });
  const owner = auth.identity?.membership.role === "owner";
  const [reauthenticationId, setReauthenticationId] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [enabled, setEnabled] = useState(false);
  const [retainDays, setRetainDays] = useState("30");
  const verify = async (credentials: ReauthenticationCredentials) => {
    setSaving(true);
    setSaveError(undefined);
    try {
      const result = await securityApi.reauthenticate({
        password: credentials.password,
        ...(credentials.secondFactor === undefined
          ? {}
          : {
              secondFactor: /^\d{6}$/u.test(credentials.secondFactor)
                ? { method: "totp" as const, code: credentials.secondFactor }
                : { method: "recovery_code" as const, code: credentials.secondFactor },
            }),
      });
      setReauthenticationId(result.reauthenticationId);
    } catch (caught) {
      setSaveError(apiErrorMessage(caught));
    } finally {
      setSaving(false);
    }
  };
  const savePolicy = async () => {
    if (
      selected === undefined ||
      reauthenticationId === undefined ||
      policy.data?.state === undefined
    )
      return;
    const days = Number(retainDays);
    if (!Number.isSafeInteger(days) || days < 1 || days > 3650) {
      setSaveError("Saklama günü 1 ile 3650 arasında tam sayı olmalıdır.");
      return;
    }
    setSaving(true);
    setSaveError(undefined);
    try {
      await operationsApi.saveRetentionPolicy({
        projectId: selected,
        confirmationProjectId: selected,
        expectedRevision: policy.data.state === "saved" ? policy.data.revision : 0,
        enabled,
        rules: [{ target: "images", retainDays: days }],
        reauthenticationId,
      });
      setReauthenticationId(undefined);
      await policy.refetch();
      await preview.refetch();
    } catch (caught) {
      setSaveError(apiErrorMessage(caught));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="page stack">
      <PageHeader
        title="Operasyon ayarları"
        description="Yalnızca yetkili sunucu projeksiyonları ve güvenli politika durumları gösterilir."
      />
      <section className="card stack">
        <h2>Yönetim kısayolları</h2>
        <ul>
          <li>
            <a href="/ekip">Üyeler ve roller</a>
          </li>
          <li>
            <a href="/ayarlar/api-anahtarlari">Kapsamlı API anahtarları</a>
          </li>
          <li>
            <a href="/ayarlar/saglayicilar">Git, Cloudflare ve SQL sağlayıcı entegrasyonları</a>
          </li>
          <li>
            <a href="/sunucular">Kayıtlı sunucu durumu</a>
          </li>
        </ul>
        <p className="muted">
          Kimlik bilgileri yalnızca yazılır veya maskelenir; bu sayfada gizli değer, root denetimi
          ya da kabuk erişimi yoktur.
        </p>
      </section>
      <AlertBanner heading="Düz metin yedek sorumluluğu" tone="warning">
        <p>{backupCopy.ownerNotice}</p>
        <p>
          Yalıtım, sınırlı izinler, sağlama toplamı/tarama ve yeni hedefe geri yükleme doğrulaması
          sahip sorumluluğundadır.
        </p>
      </AlertBanner>
      {projects.isLoading ? <Skeleton label="Projeler yükleniyor" /> : null}
      {projects.isError ? <ErrorState onRetry={() => void projects.refetch()} /> : null}
      {projects.data === undefined || selected === undefined ? null : (
        <section aria-labelledby="retention-policy-heading" className="card stack">
          <h2 id="retention-policy-heading">Saklama politikası</h2>
          <SelectField
            label="Proje"
            onChange={(event) => setProjectId(event.currentTarget.value)}
            value={selected}
          >
            {projects.data.items.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </SelectField>
          {policy.isLoading || preview.isLoading ? (
            <Skeleton label="Saklama politikası yükleniyor" />
          ) : null}
          {policy.isError || preview.isError ? (
            <AlertBanner heading="Saklama durumu alınamadı" tone="error">
              <p>{apiErrorMessage(policy.error ?? preview.error)}</p>
            </AlertBanner>
          ) : null}
          {policy.data?.state === "unset" ? (
            <AlertBanner heading="Saklama politikası kapalı" tone="warning">
              <p>
                Geçerli bir sahip politikası yok; süre veya kota varsayılan olarak etkin değildir.
              </p>
            </AlertBanner>
          ) : null}
          {policy.data?.state === "saved" ? (
            <>
              <StatusBadge
                status={policy.data.enabled ? "active" : "warning"}
                label={policy.data.enabled ? "Politika kaydedildi" : "Politika devre dışı"}
              />
              <p>Sürüm: {policy.data.revision}</p>
              <ul>
                {policy.data.rules.map((rule) => (
                  <li key={rule.target}>
                    {rule.target}: {rule.retainDays} gün
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {preview.data?.state === "disabled" ? (
            <AlertBanner heading="Temizlik başlatılamaz" tone="warning">
              <p>
                {preview.data.reason === "owner_policy_required"
                  ? "Sahip politikası etkin değil."
                  : "Yetkili canlı aday envanteri doğrulanamadı."}
              </p>
            </AlertBanner>
          ) : null}
          {owner ? (
            <>
              <fieldset>
                <legend>Sahip politika değişikliği</legend>
                <label>
                  <input
                    checked={enabled}
                    onChange={(event) => setEnabled(event.currentTarget.checked)}
                    type="checkbox"
                  />{" "}
                  Politikayı etkinleştir
                </label>
                <label className="field">
                  Görsel saklama günü
                  <input
                    inputMode="numeric"
                    max="3650"
                    min="1"
                    onChange={(event) => setRetainDays(event.currentTarget.value)}
                    value={retainDays}
                  />
                </label>
              </fieldset>
              <Button onClick={() => setReauthenticationId("")}>
                Politikayı yeniden doğrula ve kaydet
              </Button>
            </>
          ) : (
            <AlertBanner heading="Sadece sahip değiştirebilir" tone="warning">
              <p>Bu durumu görebilirsiniz; politika kaydı veya temizlik isteği gönderemezsiniz.</p>
            </AlertBanner>
          )}
        </section>
      )}
      {reauthenticationId === undefined ? null : reauthenticationId === "" ? (
        <ReauthenticationDialog
          actionName="saklama politikasını kaydet"
          error={saveError}
          onOpenChange={(open) => {
            if (!open) setReauthenticationId(undefined);
          }}
          onVerify={verify}
          open
          requireSecondFactor={auth.identity?.user.totpEnabled === true}
          submitting={saving}
        />
      ) : (
        <DestructiveConfirmDialog
          confirmationLabel={selected}
          consequence="Bu politika yeni, sürümlü bir sahip kaydı oluşturur. Temizlik yine ancak ayrı bir doğrulanmış önizleme ve iş sonucu ile yapılabilir."
          error={saveError}
          finalAction="Politikayı kaydet"
          name="Saklama politikasını kaydet"
          onConfirm={savePolicy}
          onOpenChange={(open) => {
            if (!open) setReauthenticationId(undefined);
          }}
          open
          submitting={saving}
        />
      )}
    </div>
  );
}
